import { useEffect, useMemo, useState } from "react";
import { Receipt, Plus, Search, Loader2, Send, Eye, Trash2, Download, CheckCircle2, Clock, AlertTriangle, X, FileText, CreditCard, Mail, Pencil, Image as ImageIcon, AlertCircle, MessageSquare, Sparkles, Lock } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    fetchInvoices, fetchInvoice, createInvoice, patchInvoice, reopenInvoice, deleteInvoice,
    issueInvoice, sendInvoice, registerInvoicePayment, deleteInvoicePayment,
    markInvoicePaymentMissing, autoBackfillInvoices, fetchInvoicePdfBlob,
    formatApiError,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_META = {
    draft: { label: "Kladde", icon: Pencil, bg: "bg-slate-100", text: "text-slate-700" },
    sent: { label: "Sendt", icon: Send, bg: "bg-blue-50", text: "text-blue-700" },
    partially_paid: { label: "Delvist betalt", icon: Clock, bg: "bg-amber-50", text: "text-amber-700" },
    paid: { label: "Betalt", icon: CheckCircle2, bg: "bg-emerald-50", text: "text-emerald-700" },
    overdue: { label: "Forfalden", icon: AlertTriangle, bg: "bg-red-50", text: "text-red-700" },
    cancelled: { label: "Annulleret", icon: X, bg: "bg-slate-100", text: "text-slate-500" },
};

const formatKr = (n) => `${Number(n || 0).toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr.`;
const formatDate = (iso) => {
    if (!iso) return "—";
    const d = iso.includes("T") ? new Date(iso) : new Date(iso + "T00:00:00");
    return d.toLocaleDateString("da-DK", { day: "numeric", month: "short", year: "numeric" });
};

function StatusBadge({ status }) {
    const meta = STATUS_META[status] || STATUS_META.draft;
    const Icon = meta.icon;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold ${meta.bg} ${meta.text}`}>
            <Icon className="w-3 h-3" /> {meta.label}
        </span>
    );
}

const emptyLine = () => ({ description: "", qty: 1, unit: "stk", unit_price: 0, vat_rate: 25, kind: "other" });

function InvoiceFormModal({ open, onClose, onSaved, initial }) {
    const isEdit = !!initial?.id;
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(initial || {
        customer_name: "", customer_address: "", customer_zip: "", customer_city: "", customer_country: "Danmark",
        customer_email: "", customer_phone: "", customer_cvr: "",
        job_id: "", job_description: "",
        issue_date: new Date().toISOString().slice(0, 10),
        due_date: "",
        payment_info: "", note: "",
        line_items: [emptyLine()],
    });

    useEffect(() => {
        if (open && initial) setForm(initial);
        if (open && !initial) {
            setForm({
                customer_name: "", customer_address: "", customer_zip: "", customer_city: "", customer_country: "Danmark",
                customer_email: "", customer_phone: "", customer_cvr: "",
                job_id: "", job_description: "",
                issue_date: new Date().toISOString().slice(0, 10),
                due_date: "",
                payment_info: "", note: "",
                line_items: [emptyLine()],
            });
        }
    }, [open, initial]);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setLine = (i, k, v) => setForm((f) => {
        const items = [...(f.line_items || [])];
        items[i] = { ...items[i], [k]: v };
        return { ...f, line_items: items };
    });
    const addLine = () => setForm((f) => ({ ...f, line_items: [...(f.line_items || []), emptyLine()] }));
    const delLine = (i) => setForm((f) => {
        const items = [...(f.line_items || [])];
        items.splice(i, 1);
        return { ...f, line_items: items.length ? items : [emptyLine()] };
    });

    const totals = useMemo(() => {
        let subtotal = 0, vat = 0;
        (form.line_items || []).forEach((li) => {
            const s = (Number(li.qty) || 0) * (Number(li.unit_price) || 0);
            const v = s * (Number(li.vat_rate) || 0) / 100;
            subtotal += s; vat += v;
        });
        return { subtotal, vat, total: subtotal + vat };
    }, [form.line_items]);

    const [reopening, setReopening] = useState(false);
    const invStatus = initial?.status || "draft";
    const isFinal = ["sent", "paid", "overdue", "partially_paid"].includes(invStatus);
    const approvedDraft = isEdit && invStatus === "draft" && !!initial?.approved_at;
    const locked = isEdit && (approvedDraft || isFinal);
    const handleReopen = async () => {
        setReopening(true);
        try {
            const saved = await reopenInvoice(initial.id);
            toast.success("Genåbnet til rettelse");
            setForm(saved);
            onSaved(saved);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setReopening(false);
        }
    };

    const handleSubmit = async () => {
        if (!form.customer_name?.trim()) {
            toast.error("Indtast kundens navn.");
            return;
        }        const cleanItems = (form.line_items || []).filter((li) => li.description?.trim() && Number(li.qty) > 0);
        if (cleanItems.length === 0) {
            toast.error("Tilføj mindst én linje.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                line_items: cleanItems.map((li) => ({
                    ...li,
                    qty: Number(li.qty) || 0,
                    unit_price: Number(li.unit_price) || 0,
                    vat_rate: Number(li.vat_rate) || 0,
                })),
            };
            if (!payload.customer_email) delete payload.customer_email;
            const saved = isEdit ? await patchInvoice(initial.id, payload) : await createInvoice(payload);
            toast.success(isEdit ? "Faktura opdateret" : "Kladde oprettet");
            onSaved(saved);
            onClose();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="invoice-form-modal">
                <DialogTitle className="sr-only">{isEdit ? "Rediger faktura" : "Ny faktura (kladde)"}</DialogTitle>
                <DialogDescription className="sr-only">Formular til oprettelse eller redigering af faktura med kunde, jobdetaljer og linjer.</DialogDescription>
                <h2 className="text-xl font-bold text-slate-900 mb-4 inline-flex items-center gap-2"><Receipt className="w-5 h-5 text-blue-600" />{isEdit ? "Rediger faktura" : "Ny faktura (kladde)"}</h2>

                {form.data_quality_warning && (
                    <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2.5 text-sm" data-testid="invoice-dq-warning">
                        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <p className="font-semibold text-amber-900">{form.data_quality_warning}</p>
                            <p className="text-xs text-amber-700 mt-0.5">Auto-oprettet fra job uden komplet tids- eller materialedata. Gennemgå og rediger linjerne nedenfor før du sender.</p>
                        </div>
                    </div>
                )}

                {locked && (
                    <div className="mb-4 bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-start gap-2.5 text-sm" data-testid="invoice-locked-banner">
                        <Lock className="w-4 h-4 text-slate-600 mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <p className="font-semibold text-slate-800">Låst efter godkendelse</p>
                            <p className="text-xs text-slate-600 mt-0.5">{approvedDraft ? "Tid, materialer og priser er låst. Vælg “Genåbn til rettelse” for at ændre fakturagrundlaget." : "Fakturaen er afsendt/betalt — fakturagrundlaget kan ikke ændres."}</p>
                        </div>
                        {approvedDraft && (
                            <Button type="button" size="sm" variant="outline" onClick={handleReopen} disabled={reopening} data-testid="invoice-reopen-btn" className="shrink-0 border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100">
                                {reopening ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Lock className="w-3.5 h-3.5 mr-1.5" />}Genåbn til rettelse
                            </Button>
                        )}
                    </div>
                )}

                <fieldset disabled={locked} className="space-y-4 border-0 p-0 m-0 min-w-0">
                    {/* Customer */}
                    <div>
                        <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Kunde</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label className="text-xs">Navn *</Label>
                                <Input value={form.customer_name || ""} onChange={(e) => setField("customer_name", e.target.value)} data-testid="invoice-customer_name" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Email</Label>
                                <Input type="email" value={form.customer_email || ""} onChange={(e) => setField("customer_email", e.target.value)} data-testid="invoice-customer_email" />
                            </div>
                            <div className="space-y-1 sm:col-span-2">
                                <Label className="text-xs">Adresse (vej + nr.)</Label>
                                <Input value={form.customer_address || ""} onChange={(e) => setField("customer_address", e.target.value)} data-testid="invoice-customer_address" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Postnummer</Label>
                                <Input value={form.customer_zip || ""} onChange={(e) => setField("customer_zip", e.target.value)} data-testid="invoice-customer_zip" placeholder="5000" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">By</Label>
                                <Input value={form.customer_city || ""} onChange={(e) => setField("customer_city", e.target.value)} data-testid="invoice-customer_city" placeholder="Odense C" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Land</Label>
                                <Input value={form.customer_country || ""} onChange={(e) => setField("customer_country", e.target.value)} data-testid="invoice-customer_country" placeholder="Danmark" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Telefon</Label>
                                <Input value={form.customer_phone || ""} onChange={(e) => setField("customer_phone", e.target.value)} data-testid="invoice-customer_phone" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">CVR (valgfri)</Label>
                                <Input value={form.customer_cvr || ""} onChange={(e) => setField("customer_cvr", e.target.value)} data-testid="invoice-customer_cvr" />
                            </div>
                        </div>
                    </div>

                    {/* Job */}
                    <div>
                        <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Job</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div className="space-y-1">
                                <Label className="text-xs">Job-ID</Label>
                                <Input value={form.job_id || ""} onChange={(e) => setField("job_id", e.target.value)} data-testid="invoice-job_id" />
                            </div>
                            <div className="space-y-1 sm:col-span-2">
                                <Label className="text-xs">Jobbeskrivelse</Label>
                                <Input value={form.job_description || ""} onChange={(e) => setField("job_description", e.target.value)} data-testid="invoice-job_description" />
                            </div>
                        </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs">Fakturadato</Label>
                            <Input type="date" value={form.issue_date || ""} onChange={(e) => setField("issue_date", e.target.value)} data-testid="invoice-issue_date" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Forfaldsdato</Label>
                            <Input type="date" value={form.due_date || ""} onChange={(e) => setField("due_date", e.target.value)} placeholder="Automatisk (8 dage)" data-testid="invoice-due_date" />
                        </div>
                    </div>

                    {/* Line items */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Linjer</h3>
                            <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="invoice-add-line"><Plus className="w-3 h-3 mr-1" />Tilføj linje</Button>
                        </div>
                        <div className="space-y-2" data-testid="invoice-lines">
                            {(form.line_items || []).map((li, i) => (
                                <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`invoice-line-${i}`}>
                                    <div className="col-span-12 sm:col-span-5">
                                        <Label className="text-[10px] text-slate-500">Beskrivelse</Label>
                                        <Input value={li.description} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`invoice-line-${i}-desc`} />
                                    </div>
                                    <div className="col-span-3 sm:col-span-1">
                                        <Label className="text-[10px] text-slate-500">Antal</Label>
                                        <Input type="number" step="0.5" min="0" value={li.qty} onChange={(e) => setLine(i, "qty", e.target.value)} data-testid={`invoice-line-${i}-qty`} />
                                    </div>
                                    <div className="col-span-3 sm:col-span-1">
                                        <Label className="text-[10px] text-slate-500">Enhed</Label>
                                        <Input value={li.unit || ""} onChange={(e) => setLine(i, "unit", e.target.value)} data-testid={`invoice-line-${i}-unit`} />
                                    </div>
                                    <div className="col-span-3 sm:col-span-2">
                                        <Label className="text-[10px] text-slate-500">À pris</Label>
                                        <Input type="number" step="0.01" min="0" value={li.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} data-testid={`invoice-line-${i}-price`} />
                                    </div>
                                    <div className="col-span-3 sm:col-span-1">
                                        <Label className="text-[10px] text-slate-500">Moms%</Label>
                                        <Input type="number" min="0" max="100" value={li.vat_rate ?? 25} onChange={(e) => setLine(i, "vat_rate", e.target.value)} data-testid={`invoice-line-${i}-vat`} />
                                    </div>
                                    <div className="col-span-9 sm:col-span-1 flex items-center justify-end text-sm font-semibold text-slate-900 truncate">
                                        {formatKr((Number(li.qty) || 0) * (Number(li.unit_price) || 0) * (1 + (Number(li.vat_rate) || 0) / 100))}
                                    </div>
                                    <div className="col-span-3 sm:col-span-1 flex justify-end">
                                        <button type="button" onClick={() => delLine(i)} className="w-9 h-9 rounded-lg hover:bg-red-50 text-red-500 inline-flex items-center justify-center" data-testid={`invoice-line-${i}-delete`}><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Totals */}
                    <div className="bg-slate-50 rounded-xl p-4 space-y-1.5 text-sm" data-testid="invoice-totals">
                        <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="font-medium text-slate-900">{formatKr(totals.subtotal)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-600">Moms</span><span className="font-medium text-slate-900">{formatKr(totals.vat)}</span></div>
                        <div className="flex justify-between pt-2 border-t border-slate-200"><span className="font-bold text-slate-900">Total</span><span className="font-bold text-lg text-slate-900">{formatKr(totals.total)}</span></div>
                    </div>

                    {/* Payment info + note */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs">Betalingsinformation</Label>
                            <textarea rows={3} value={form.payment_info || ""} onChange={(e) => setField("payment_info", e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" data-testid="invoice-payment_info" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Note</Label>
                            <textarea rows={3} value={form.note || ""} onChange={(e) => setField("note", e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" data-testid="invoice-note" />
                        </div>
                    </div>
                </fieldset>

                <div className="flex justify-end gap-2 mt-6">
                    <Button variant="outline" onClick={onClose}>Annullér</Button>
                    <Button onClick={handleSubmit} disabled={saving || locked} data-testid="invoice-form-submit" className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}{isEdit ? "Gem ændringer" : "Opret kladde"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function PdfPreviewModal({ invoice, onClose }) {
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!invoice?.id) return undefined;
        let cancelled = false;
        let currentUrl = null;
        setLoading(true);
        setError("");
        setBlobUrl(null);
        (async () => {
            try {
                const blob = await fetchInvoicePdfBlob(invoice.id);
                if (cancelled) return;
                currentUrl = URL.createObjectURL(blob);
                setBlobUrl(currentUrl);
            } catch (e) {
                if (!cancelled) setError(formatApiError(e) || "Kunne ikke hente PDF.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            if (currentUrl) URL.revokeObjectURL(currentUrl);
        };
    }, [invoice?.id]);

    const handleDownload = async () => {
        try {
            const blob = await fetchInvoicePdfBlob(invoice.id);
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `faktura-${(invoice.number || "kladde").replace("/", "-")}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-4xl max-h-[90vh] p-2" data-testid="invoice-pdf-modal">
                <DialogTitle className="sr-only">Forhåndsvisning af faktura {invoice?.number || "kladde"}</DialogTitle>
                <DialogDescription className="sr-only">PDF-forhåndsvisning af fakturaen.</DialogDescription>
                <div className="flex items-center justify-between p-3 border-b border-slate-100">
                    <h3 className="font-bold text-slate-900">Faktura {invoice?.number || "kladde"}</h3>
                    <button onClick={handleDownload} className="text-xs inline-flex items-center gap-1 text-blue-600 hover:text-blue-700" data-testid="invoice-pdf-download"><Download className="w-3 h-3" />Download PDF</button>
                </div>
                {loading && (
                    <div className="w-full h-[78vh] flex items-center justify-center" data-testid="invoice-pdf-loading">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                        <span className="ml-2 text-sm text-slate-500">Henter PDF…</span>
                    </div>
                )}
                {error && !loading && (
                    <div className="w-full h-[78vh] flex flex-col items-center justify-center gap-2 text-center px-6" data-testid="invoice-pdf-error">
                        <AlertCircle className="w-8 h-8 text-red-500" />
                        <p className="text-sm font-semibold text-red-700">Kunne ikke indlæse PDF</p>
                        <p className="text-xs text-slate-500 max-w-md">{error}</p>
                    </div>
                )}
                {blobUrl && !loading && !error && (
                    <iframe title="Faktura PDF" src={blobUrl} className="w-full h-[78vh] rounded-lg" data-testid="invoice-pdf-iframe" />
                )}
            </DialogContent>
        </Dialog>
    );
}

function PaymentModal({ invoice, onClose, onSaved }) {
    const [saving, setSaving] = useState(false);
    const remaining = (Number(invoice?.total) || 0) - (Number(invoice?.paid_amount) || 0);
    const [form, setForm] = useState({ amount: remaining > 0 ? remaining : 0, paid_at: new Date().toISOString().slice(0, 10), method: "bank", reference: "", note: "" });

    useEffect(() => {
        setForm({ amount: remaining > 0 ? remaining : 0, paid_at: new Date().toISOString().slice(0, 10), method: "bank", reference: "", note: "" });
    }, [invoice]);

    const submit = async () => {
        try {
            setSaving(true);
            const res = await registerInvoicePayment(invoice.id, {
                amount: Number(form.amount),
                paid_at: form.paid_at,
                method: form.method,
                reference: form.reference,
                note: form.note,
            });
            toast.success(`Betaling registreret. Status: ${STATUS_META[res.status]?.label || res.status}`);
            onSaved();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="invoice-payment-modal">
                <DialogTitle className="sr-only">Registrer betaling</DialogTitle>
                <DialogDescription className="sr-only">Registrer en manuel betaling med beløb, dato og metode.</DialogDescription>
                <h2 className="text-lg font-bold text-slate-900 mb-1 inline-flex items-center gap-2"><CreditCard className="w-5 h-5 text-emerald-600" />Registrer betaling</h2>
                <p className="text-sm text-slate-500 mb-4">Faktura {invoice?.number} · Restbeløb: <b>{formatKr(remaining)}</b></p>
                <div className="space-y-3">
                    <div className="space-y-1">
                        <Label className="text-xs">Beløb</Label>
                        <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} data-testid="payment-amount" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs">Dato</Label>
                            <Input type="date" value={form.paid_at} onChange={(e) => setForm((f) => ({ ...f, paid_at: e.target.value }))} data-testid="payment-paid_at" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Metode</Label>
                            <select value={form.method} onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))} data-testid="payment-method" className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-200">
                                <option value="bank">Bankoverførsel</option>
                                <option value="mobilepay">MobilePay</option>
                                <option value="kontant">Kontant</option>
                                <option value="kort">Kort</option>
                                <option value="andet">Andet</option>
                            </select>
                        </div>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">Reference (valgfri)</Label>
                        <Input value={form.reference} onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} placeholder="fx bogføringsnr." data-testid="payment-reference" />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">Note (valgfri)</Label>
                        <Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} data-testid="payment-note" />
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <Button variant="outline" onClick={onClose}>Annullér</Button>
                    <Button onClick={submit} disabled={saving || Number(form.amount) <= 0} data-testid="payment-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}Registrer betaling
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function SendModal({ invoice, onClose, onSent }) {
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ recipient: invoice?.customer_email || "", subject: "", message: "" });

    useEffect(() => {
        setForm({ recipient: invoice?.customer_email || "", subject: "", message: "" });
    }, [invoice]);

    const submit = async () => {
        try {
            setSaving(true);
            const payload = {};
            if (form.recipient) payload.recipient = form.recipient;
            if (form.subject) payload.subject = form.subject;
            if (form.message) payload.message = `<div style="font-family:Arial,sans-serif;font-size:14px;line-height:1.6;color:#0f172a;">${form.message.replace(/\n/g, "<br/>")}</div>`;
            await sendInvoice(invoice.id, payload);
            toast.success(`Faktura sendt til ${form.recipient}`);
            onSent();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="invoice-send-modal">
                <DialogTitle className="sr-only">Send faktura til kunde</DialogTitle>
                <DialogDescription className="sr-only">Send fakturaen som PDF-vedhæftning via email.</DialogDescription>
                <h2 className="text-lg font-bold text-slate-900 mb-1 inline-flex items-center gap-2"><Mail className="w-5 h-5 text-blue-600" />Send faktura til kunde</h2>
                <p className="text-sm text-slate-500 mb-4">Faktura {invoice?.number || "(udstedes automatisk)"} sendes som PDF-vedhæftning.</p>
                <div className="space-y-3">
                    <div className="space-y-1">
                        <Label className="text-xs">Modtager-email *</Label>
                        <Input type="email" value={form.recipient} onChange={(e) => setForm((f) => ({ ...f, recipient: e.target.value }))} data-testid="send-recipient" />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">Emne (valgfrit — udfyldes automatisk)</Label>
                        <Input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} data-testid="send-subject" />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">Personlig besked (valgfri)</Label>
                        <textarea rows={4} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" data-testid="send-message" />
                    </div>
                    {!form.recipient && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 text-xs text-red-700" data-testid="send-no-email-warning">
                            ⚠️ Kundens email mangler — kan ikke sende. Tilføj først en email på kunde-profilen.
                        </div>
                    )}
                    {invoice?.customer_phone && (
                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 flex items-start gap-2" data-testid="send-sms-placeholder">
                            <MessageSquare className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-[11px] font-semibold text-slate-700">SMS til {invoice.customer_phone}</p>
                                <p className="text-[10px] text-slate-500">SMS ikke konfigureret endnu</p>
                            </div>
                            <Button disabled size="sm" variant="outline" className="text-[10px] h-7 opacity-50">Send SMS</Button>
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <Button variant="outline" onClick={onClose}>Annullér</Button>
                    <Button onClick={submit} disabled={saving || !form.recipient} data-testid="send-submit" className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Send faktura
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// === Days-until-due badge ===
function DaysUntilDueBadge({ days, status }) {
    if (days === null || days === undefined) return null;
    if (status === "paid" || status === "cancelled" || status === "draft") return null;
    if (days > 0) return <span className="text-[10px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full font-medium">Betales om {days} dage</span>;
    if (days === 0) return <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full font-medium">Forfalder i dag</span>;
    return <span className="text-[10px] text-red-700 bg-red-50 px-1.5 py-0.5 rounded-full font-medium animate-pulse">Forfalden {-days} dage</span>;
}

// === Type-SLET delete confirm modal ===
function DeleteInvoiceConfirmModal({ invoice, onClose, onDeleted }) {
    const [confirm, setConfirm] = useState("");
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    if (!invoice) return null;
    const isFinalised = ["sent", "paid", "overdue"].includes(invoice.status);
    const canDelete = confirm.trim().toUpperCase() === "SLET";
    const submit = async () => {
        if (!canDelete) return;
        try {
            setBusy(true);
            await deleteInvoice(invoice.id, { confirm: "SLET", reason });
            toast.success("Faktura slettet (soft-delete: status=annulleret)");
            onDeleted();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-md" data-testid="invoice-delete-modal">
                <DialogTitle className="text-red-700 inline-flex items-center gap-2"><AlertCircle className="w-5 h-5" />Slet faktura</DialogTitle>
                <DialogDescription>
                    Du er ved at slette faktura <b>{invoice.number || invoice.id.slice(0, 8)}</b>. Dette er en <i>soft-delete</i> — fakturaen får status &quot;annulleret&quot; og fjernes fra listen, men bevares i audit-log.
                </DialogDescription>
                {isFinalised && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 my-3" data-testid="invoice-delete-warning">
                        <p className="text-xs font-bold text-red-700 mb-1">⚠️ Denne faktura er allerede sendt eller betalt</p>
                        <p className="text-[11px] text-red-600 leading-snug">Det kan have juridiske og bogføringsmæssige konsekvenser at slette den. Overvej i stedet at lave en kreditnota.</p>
                    </div>
                )}
                <div className="space-y-3 mt-2">
                    <div>
                        <Label className="text-xs">Skriv <code className="text-red-700 bg-red-50 px-1.5 py-0.5 rounded">SLET</code> for at bekræfte</Label>
                        <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="SLET" className="font-mono mt-1" autoFocus data-testid="invoice-delete-confirm-input" />
                    </div>
                    <div>
                        <Label className="text-xs">Årsag (valgfri)</Label>
                        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="fx Dobbelt-faktura" className="mt-1" data-testid="invoice-delete-reason" />
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <Button variant="outline" onClick={onClose} disabled={busy}>Annullér</Button>
                    <Button onClick={submit} disabled={!canDelete || busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="invoice-delete-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}Slet faktura
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// === Edit warning modal ===
function EditWarningModal({ invoice, onClose, onConfirm }) {
    if (!invoice) return null;
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-md" data-testid="invoice-edit-warning-modal">
                <DialogTitle className="text-amber-700 inline-flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Faktura er allerede udstedt</DialogTitle>
                <DialogDescription>
                    Faktura <b>{invoice.number}</b> er sendt til kunden. Ændringer her påvirker IKKE den allerede afsendte PDF — overvej i stedet at sende en kreditnota.
                </DialogDescription>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 my-3">
                    <p className="text-[11px] text-amber-800">Kun status, note, betalingsinfo og forfaldsdato kan ændres efter udstedelse.</p>
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <Button variant="outline" onClick={onClose}>Annullér</Button>
                    <Button onClick={onConfirm} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="invoice-edit-warning-continue">
                        Fortsæt med redigering
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function Fakturaer() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState(searchParams.get("filter") === "overdue" ? "overdue" : "all");
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [pdfPreview, setPdfPreview] = useState(null);
    const [paymentFor, setPaymentFor] = useState(null);
    const [sendFor, setSendFor] = useState(null);
    const [deleteFor, setDeleteFor] = useState(null);
    const [editWarnFor, setEditWarnFor] = useState(null);

    const load = async () => {
        try {
            setLoading(true);
            setError("");
            const params = {};
            if (statusFilter !== "all") params.status = statusFilter;
            if (search) params.q = search;
            const d = await fetchInvoices(params);
            setItems(d.items || []);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [statusFilter]);

    const filtered = useMemo(() => {
        if (!search) return items;
        const q = search.toLowerCase();
        return items.filter((i) => [i.number, i.customer_name, i.customer_email, i.job_id].some((v) => (v || "").toLowerCase().includes(q)));
    }, [items, search]);

    const stats = useMemo(() => {
        const total = items.reduce((s, i) => s + (i.total || 0), 0);
        const outstanding = items
            .filter((i) => i.status === "sent" || i.status === "overdue")
            .reduce((s, i) => s + ((i.total || 0) - (i.paid_amount || 0)), 0);
        const paid = items.filter((i) => i.status === "paid").length;
        const overdue = items.filter((i) => i.status === "overdue").length;
        return { total, outstanding, paid, overdue };
    }, [items]);

    const handleDelete = (inv) => setDeleteFor(inv);

    const handleEditClick = async (inv) => {
        const full = await fetchInvoice(inv.id);
        if (full.status !== "draft") {
            setEditWarnFor(full);
        } else {
            setEditing(full);
            setFormOpen(true);
        }
    };

    const handleMarkPaymentMissing = async (inv) => {
        if (!window.confirm(`Markér faktura "${inv.number}" som "Mangler betaling"?`)) return;
        try {
            await markInvoicePaymentMissing(inv.id);
            toast.success("Markeret som 'Mangler betaling'");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const handleIssue = async (inv) => {
        try {
            const res = await issueInvoice(inv.id);
            toast.success(`Faktura udstedt: ${res.number}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="fakturaer-page">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900 inline-flex items-center gap-2"><Receipt className="w-7 h-7 text-blue-600" />Fakturaer</h1>
                    <p className="text-sm text-slate-500 mt-1">Opret, send og registrer betaling på fakturaer.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        data-testid="invoice-backfill-btn"
                        onClick={async () => {
                            try {
                                const res = await autoBackfillInvoices();
                                if (res.created > 0) {
                                    toast.success(`${res.created} ny${res.created === 1 ? "" : "e"} fakturakladde${res.created === 1 ? "" : "r"} oprettet fra færdige jobs`);
                                    load();
                                } else {
                                    toast.info(`Ingen nye fakturaer. ${res.skipped_existing} job(s) havde allerede en faktura.`);
                                }
                            } catch (e) {
                                toast.error(formatApiError(e));
                            }
                        }}
                        className="rounded-full"
                    >
                        <Sparkles className="w-4 h-4 mr-2" />Opret manglende
                    </Button>
                    <Button variant="outline" onClick={() => navigate("/dashboard/faktura-klar/template")} data-testid="invoice-template-btn" className="rounded-full"><ImageIcon className="w-4 h-4 mr-2" />Faktura-skabelon</Button>
                    <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="invoice-new-btn" className="rounded-full bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white"><Plus className="w-4 h-4 mr-2" />Ny faktura</Button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="bg-white rounded-2xl border border-slate-200/70 p-4" data-testid="stat-total">
                    <p className="text-xs text-slate-500">Total faktureret</p>
                    <p className="text-xl font-bold text-slate-900 mt-1">{formatKr(stats.total)}</p>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200/70 p-4" data-testid="stat-outstanding">
                    <p className="text-xs text-slate-500">Udestående</p>
                    <p className="text-xl font-bold text-amber-600 mt-1">{formatKr(stats.outstanding)}</p>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200/70 p-4" data-testid="stat-paid">
                    <p className="text-xs text-slate-500">Betalte</p>
                    <p className="text-xl font-bold text-emerald-600 mt-1">{stats.paid}</p>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200/70 p-4" data-testid="stat-overdue">
                    <p className="text-xs text-slate-500">Forfaldne</p>
                    <p className="text-xl font-bold text-red-600 mt-1">{stats.overdue}</p>
                </div>
            </div>

            {/* Toolbar */}
            <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4 mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Søg fakturanr, kunde, email…" className="pl-10" data-testid="invoice-search" />
                    </div>
                    <div className="inline-flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="invoice-status-tabs">
                        {[
                            { id: "all", label: "Alle" },
                            { id: "draft", label: "Kladder" },
                            { id: "sent", label: "Sendt" },
                            { id: "paid", label: "Betalt" },
                            { id: "overdue", label: "Forfalden" },
                        ].map((t) => (
                            <button key={t.id} onClick={() => setStatusFilter(t.id)} data-testid={`invoice-tab-${t.id}`} className={`text-xs px-3 py-1.5 rounded-md font-medium transition ${statusFilter === t.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>{t.label}</button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Table */}
            {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3 text-sm text-red-700">{error}</div>}
            <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm overflow-hidden">
                {loading ? (
                    <div className="py-16 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
                ) : filtered.length === 0 ? (
                    <div className="py-16 text-center" data-testid="invoice-empty">
                        <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                        <p className="text-sm text-slate-500">Ingen fakturaer endnu. Opret din første kladde øverst til højre.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="invoice-table">
                            <thead>
                                <tr className="text-[11px] uppercase text-slate-400 border-b border-slate-100">
                                    <th className="py-3 px-4 text-left font-medium">Faktura</th>
                                    <th className="py-3 px-4 text-left font-medium">Kunde</th>
                                    <th className="py-3 px-4 text-left font-medium">Forfald</th>
                                    <th className="py-3 px-4 text-right font-medium">Total</th>
                                    <th className="py-3 px-4 text-right font-medium">Betalt</th>
                                    <th className="py-3 px-4 text-left font-medium">Status</th>
                                    <th className="py-3 px-4 text-right font-medium">Handlinger</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {filtered.map((i) => (
                                    <tr key={i.id} data-testid={`invoice-row-${i.id}`} className="hover:bg-slate-50/50 transition">
                                        <td className="py-3 px-4">
                                            <p className="font-medium text-slate-900">{i.number || <span className="text-slate-400 italic">kladde</span>}</p>
                                            <p className="text-[11px] text-slate-500">{formatDate(i.issue_date)}</p>
                                        </td>
                                        <td className="py-3 px-4">
                                            <p className="font-medium text-slate-900">{i.customer_name || "—"}</p>
                                            <p className="text-[11px] text-slate-500">{i.customer_email || ""}</p>
                                        </td>
                                        <td className="py-3 px-4">
                                            <p className="text-slate-700">{formatDate(i.due_date)}</p>
                                            <div className="mt-0.5"><DaysUntilDueBadge days={i.days_until_due} status={i.status} /></div>
                                        </td>
                                        <td className="py-3 px-4 text-right font-semibold text-slate-900">{formatKr(i.total)}</td>
                                        <td className="py-3 px-4 text-right text-slate-700">{formatKr(i.paid_amount || 0)}</td>
                                        <td className="py-3 px-4">
                                            <StatusBadge status={i.status} />
                                            {i.payment_missing && <p className="text-[10px] text-red-700 bg-red-50 mt-0.5 px-1.5 py-0.5 rounded-full font-medium inline-block" data-testid={`invoice-payment-missing-${i.id}`}>Mangler betaling</p>}
                                        </td>
                                        <td className="py-3 px-4">
                                            <div className="flex items-center justify-end gap-1">
                                                <button onClick={async () => setPdfPreview(await fetchInvoice(i.id))} title="Forhåndsvis PDF" className="w-8 h-8 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center" data-testid={`invoice-preview-${i.id}`}><Eye className="w-4 h-4 text-slate-600" /></button>
                                                <button onClick={() => handleEditClick(i)} title="Rediger" className="w-8 h-8 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center" data-testid={`invoice-edit-${i.id}`}><Pencil className="w-4 h-4 text-slate-600" /></button>
                                                {i.status === "draft" && (
                                                    <button onClick={() => handleIssue(i)} title="Udsted" className="px-2 h-8 rounded-lg hover:bg-blue-50 text-blue-700 text-xs font-medium" data-testid={`invoice-issue-${i.id}`}>Udsted</button>
                                                )}
                                                <button onClick={() => setSendFor(i)} title="Send til kunde" className="w-8 h-8 rounded-lg hover:bg-blue-50 inline-flex items-center justify-center" data-testid={`invoice-send-${i.id}`}><Send className="w-4 h-4 text-blue-600" /></button>
                                                {i.status !== "draft" && i.status !== "paid" && i.status !== "cancelled" && (
                                                    <>
                                                        <button onClick={() => setPaymentFor(i)} title="Marker som betalt" className="w-8 h-8 rounded-lg hover:bg-emerald-50 inline-flex items-center justify-center" data-testid={`invoice-pay-${i.id}`}><CreditCard className="w-4 h-4 text-emerald-600" /></button>
                                                        {!i.payment_missing && (
                                                            <button onClick={() => handleMarkPaymentMissing(i)} title="Mangler betaling" className="w-8 h-8 rounded-lg hover:bg-red-50 inline-flex items-center justify-center" data-testid={`invoice-missing-${i.id}`}><AlertCircle className="w-4 h-4 text-red-500" /></button>
                                                        )}
                                                    </>
                                                )}
                                                <button onClick={() => handleDelete(i)} title="Slet faktura" className="w-8 h-8 rounded-lg hover:bg-red-50 inline-flex items-center justify-center" data-testid={`invoice-delete-${i.id}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <InvoiceFormModal open={formOpen} onClose={() => { setFormOpen(false); setEditing(null); }} onSaved={load} initial={editing} />
            <PdfPreviewModal invoice={pdfPreview} onClose={() => setPdfPreview(null)} />
            <PaymentModal invoice={paymentFor} onClose={() => setPaymentFor(null)} onSaved={load} />
            <SendModal invoice={sendFor} onClose={() => setSendFor(null)} onSent={load} />
            <DeleteInvoiceConfirmModal invoice={deleteFor} onClose={() => setDeleteFor(null)} onDeleted={load} />
            <EditWarningModal invoice={editWarnFor} onClose={() => setEditWarnFor(null)} onConfirm={() => { setEditing(editWarnFor); setFormOpen(true); setEditWarnFor(null); }} />
        </div>
    );
}

// Suppress lint for unused import (provided for downstream usage)
void deleteInvoicePayment;
